import { FlatTreeControl } from '@angular/cdk/tree';
import { inject, Injectable } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { InterfaceSelectList, InterfaceUserList } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { TableService } from '@app/modules/table/table.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ROOT_PARENT_ID } from '../component/side-menu.component';
import { ICommand } from '../interfaces/Command.interface';
import { IMenuItem } from '../interfaces/MenuItem.interface';
import { MenuItem } from '../models/MenuItem';

@Injectable({
    providedIn: 'root',
})
export class SideMenuService extends TableService {
    public readonly className = 'SideMenuService';
    public readonly url = '/menu/Front_admin_menu';

    /** Шина передачи команд в компонент бокового меню */
    private commandBusSource = new Subject<ICommand>();
    public commandBus = this.commandBusSource.asObservable();

    #appService: AppService = inject(AppService);

    /** Справочники */
    private readonly _guides: {
        all_registry: InterfaceSelectList[];
        roles: InterfaceSelectList[];
        system_sections: InterfaceSelectList[];
        subjects: InterfaceUserList[];
        directory_of_posts: InterfaceUserList[];
        users: InterfaceUserList[];
    } = {
        all_registry: [],
        roles: [],
        system_sections: [],
        subjects: [],
        directory_of_posts: [],
        users: [],
    };

    /** Основная часть ссылки на реестр */
    public readonly registerLink: string = 'factor/input/';

    /** Данные дерева */
    public menuDataSource: MatTreeFlatDataSource<MenuItem, MenuItem>;

    /** Контрол дерева */
    public menuTreeControl: FlatTreeControl<MenuItem>;
    public menuTreeFlattener: MatTreeFlattener<MenuItem, MenuItem>;

    /** Корень дерева (узел содержащий данные -1 уровня, для обновления всего дерева) */
    public rootMenuItem: MenuItem;

    /**
     * Subject для подписки на изменение состояния меню
     */
    #isFixedSidenav = new BehaviorSubject<boolean>(this.#appService.getSettingsByKey('is_fixed_sidenav'));

    /**
     * Обновить состояние меню
     */
    public updateIsFixedSidenav(isFixed: boolean) {
        this.#isFixedSidenav.next(isFixed);
        this.#appService.settings.is_fixed_sidenav = isFixed;
    }

    /**
     * Получить текущее состояние меню
     */
    public getIsFixedSidenav(): Observable<boolean> {
        return this.#isFixedSidenav.asObservable();
    }

    /**
     * Получить текущее состояние меню
     */
    public get isFixedSidenav$(): Observable<boolean> {
        return this.#isFixedSidenav.asObservable();
    }

    /** Установка вида отображения меню */
    public setMenuView(data: any): Observable<any> {
        return this.http.post<any>(this.serverUrl + this.url + '/set_menu_view', data, this._httpOptions);
    }

    /**  Изменение порядка узлов дерева */
    public changeOrder(data: any): Observable<any> {
        return this.http.post<any>(this.serverUrl + this.url + '/change_order', data, this._httpOptions);
    }

    /** Получить потомков дерева */
    public getChildren(data: any) {
        return this.http.post<any>(this.serverUrl + this.url + '/get_children', data, this._httpOptions);
    }

    /**
     * Рекурсивная генерация структуры дерева
     * @param data - массив для построения дерева
     * @param parentId - id родителя
     * @param level - уровень, на котором строят узел
     * @param parentNode
     * @private
     */
    private _generateTreeData(
        data: IMenuItem[],
        parentId: number | string,
        level: number,
        parentNode = null,
    ): MenuItem[] {
        const result: MenuItem[] = [];
        data.forEach((value) => {
            switch (parentNode.type) {
                case 'factor-folder':
                    if (value?.parent_id === parentId) {
                        const node = new MenuItem(
                            Object.assign(
                                {
                                    level,
                                    parentNode,
                                },
                                value,
                            ),
                        );
                        node.setChildren(this._generateTreeData(data, node.id, level + 1, node));
                        result.push(node);
                    }
                    break;
                default:
                    if (value.parent_id === parentId) {
                        const node = new MenuItem(
                            Object.assign(
                                {
                                    level,
                                    parentNode,
                                },
                                value,
                            ),
                        );
                        node.setChildren(this._generateTreeData(data, node.id, level + 1, node));
                        result.push(node);
                    }
            }
        });
        return result;
    }

    /** Генерация структуры дерева */
    public get generateTree(): (
        data: IMenuItem[],
        parentId: number | string,
        level: number,
        parentNode: MenuItem,
    ) => MenuItem[] {
        return this._generateTreeData;
    }

    /**
     * Установка активного пункта меню по дереву меню
     * @param link - активная ссылка
     */
    public setActiveMenuItem(link: string): any[] {
        let breadcrumbs = [];
        // 1й уровень
        this.menuDataSource.data.forEach((item) => {
            item.isActive = false;

            if (item.url.length) {
                item.isActive = this.isActiveItem(link, item.url);
                if (item.isActive) {
                    breadcrumbs = breadcrumbsPush([], item);
                }
            }

            // 2й уровень
            item?.children.forEach((itemLevel2) => {
                itemLevel2.isActive = false;

                if (itemLevel2?.url?.length) {
                    itemLevel2.isActive = this.isActiveItem(link, itemLevel2.url);
                }
                if (itemLevel2.isActive) {
                    item.isActive = true;
                    breadcrumbs = breadcrumbsPush(breadcrumbsPush([], item), itemLevel2);
                }

                // 3й уровень
                itemLevel2?.children.forEach((itemLevel3) => {
                    itemLevel3.isActive = false;

                    if (itemLevel3?.url?.length) {
                        itemLevel3.isActive = this.isActiveItem(link, itemLevel3.url);
                    }
                    if (itemLevel3.isActive) {
                        item.isActive = true;
                        itemLevel2.isActive = true;
                        breadcrumbs = breadcrumbsPush(
                            breadcrumbsPush(breadcrumbsPush([], item), itemLevel2),
                            itemLevel3,
                        );
                    }
                });
            });
        });
        return breadcrumbs;
    }

    /** Найти ноду в дереве меню по заданному критерию */
    public findNode(match: (q: MenuItem) => boolean, start: MenuItem = null): MenuItem {
        const nodes = !start ? this.menuDataSource.data : start.children;
        for (const node of nodes) {
            const foundBelow = this.findNode(match, node);
            if (foundBelow) {
                return foundBelow;
            }
            if (match(node)) {
                return node;
            }
        }
        return null;
    }

    /** Заставляем дерево отобразить последние данные */
    renderTree() {
        this.menuDataSource.data = this.menuDataSource.data;
    }

    /** Установка справочников */
    public set guides(data: { [key: string]: any }) {
        Object.keys(data).forEach((key) => {
            this.guides[key] = data[key];
        });
    }

    /** Установка справочников */
    public get guides() {
        return this._guides;
    }

    /**
     * Проставление активного пункта меню для реестра
     * @param factorId ИД реестра
     */
    public setActiveMenuFactor(factorId: number): any[] {
        return this.setActiveMenuItem('factor/input/' + factorId);
    }

    private isActiveItem(currentLink: string, itemUrl): boolean {
        let url = itemUrl;
        if (url[0] === '/') {
            url = url.replace('/', '');
        }
        return currentLink === url || currentLink.startsWith(url);
    }

    /** Послать команду всем экземплярам компонента side-menu */
    public sendCommand(command: ICommand) {
        this.commandBusSource.next(command);
    }

    /**
     * Обновить дерево меню
     */
    public update() {
        const menu = this.token.getMenu();
        this.menuDataSource = new MatTreeFlatDataSource(
            this.menuTreeControl,
            this.menuTreeFlattener,
            this.generateTree(menu ?? [], ROOT_PARENT_ID, 0, this.rootMenuItem),
        );
    }
}

const breadcrumbsPush = (arr: { name: string; link?: string }[], item: MenuItem): { name: string; link?: string }[] => {
    switch (item.type) {
        case 'controller':
        case 'registry':
            arr.push({ name: item.name, link: item.url });
            break;
    }
    return arr;
};
